import { useEffect, useRef, useState } from 'react';
import { Outlet, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ClipboardList, BookOpenText, BarChart3, Bell, CircleUserRound } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import BottomTabBar from '../shared/ui/BottomTabBar';
import RestaurantSidebar from '../shared/ui/RestaurantSidebar';
import { LoadingView } from '../shared/ui';
import { listenToRestaurantOrders } from '../shared/services/orderService';
import { listenToNotifications } from '../shared/services/notificationService';
import InAppNotificationBanner from '../../../components/InAppNotificationBanner';

// Mirrors iOS RestaurantTabView: Orders (list.bullet.rectangle, badge = new
// pending orders) · Menu (menucard) · Analytics (chart.bar) · Notifications
// (bell.fill, badge = unread) · Profile (person.crop.circle). Red tint.
export default function RestaurantLayout() {
  const { user, userProfile, restaurant, restaurantId, loading, roleError } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [pendingCount, setPendingCount] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [bannerNotif, setBannerNotif] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // iOS: .badge(newOrders.count) on the Orders tab
  useEffect(() => {
    if (!restaurantId) return;
    return listenToRestaurantOrders(restaurantId, (orders) => {
      setPendingCount(orders.filter((o) => o.status === 'Pending').length);
    });
  }, [restaurantId]);

  // iOS: .badge(unreadCount) on the Notifications tab + cash-register sound
  // and an in-app banner on new notifications (after the initial snapshot).
  const lastNotifIdRef = useRef(null);
  const alertAudioRef = useRef(null);
  useEffect(() => {
    if (!user) return;
    return listenToNotifications(user.uid, (list) => {
      const unread = list.filter((n) => !n.read && !n.isRead).length;
      setUnreadCount(unread);
      const newest = list[0];
      if (lastNotifIdRef.current !== null && newest && newest.id !== lastNotifIdRef.current) {
        try {
          if (!alertAudioRef.current) alertAudioRef.current = new Audio('/sounds/cash-register.mp3');
          alertAudioRef.current.currentTime = 0;
          alertAudioRef.current.play().catch(() => {});
        } catch { /* audio blocked until user interaction */ }
        setBannerNotif(newest);
      }
      lastNotifIdRef.current = newest?.id || lastNotifIdRef.current;
    }, undefined, restaurantId);
  }, [user, restaurantId]);

  if (loading) {
    return <div className="min-h-full flex items-center justify-center bg-white"><LoadingView label="Loading…" /></div>;
  }
  if (!user || roleError) {
    return <Navigate to="/login" state={{ from: location.pathname, roleError }} replace />;
  }

  const tabs = [
    { to: '/business/orders', label: 'Orders', Icon: ClipboardList, badge: pendingCount },
    { to: '/business/menu', label: 'Menu', Icon: BookOpenText },
    { to: '/business/analytics', label: 'Analytics', Icon: BarChart3 },
    { to: '/business/notifications', label: 'Notifications', Icon: Bell, badge: unreadCount },
    { to: '/business/profile', label: 'Profile', Icon: CircleUserRound },
  ];

  return (
    <div className="min-h-full flex bg-[#F2F2F7]">
      <RestaurantSidebar
        tabs={tabs}
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed((v) => !v)}
        businessName={restaurant?.name || 'My Business'}
      />
      <div className="flex-1 min-w-0 flex flex-col">
        <div className="flex-1 pb-20 lg:pb-0 w-full max-w-lg lg:max-w-none mx-auto">
          <Outlet />
        </div>
        <div className="lg:hidden">
          <BottomTabBar tabs={tabs} />
        </div>
      </div>
      <InAppNotificationBanner
        notification={bannerNotif}
        onTap={() => { setBannerNotif(null); navigate('/business/notifications'); }}
        onDismiss={() => setBannerNotif(null)}
      />
    </div>
  );
}
